import { loadingTypes } from '@/constants/loadingTypes';

import { ApiBase } from './api-base';

/**
 * @template R
 * @typedef {import('axios').AxiosResponse<R>} AxiosResponse<R>
 */
/**
 * @typedef {import('@/models/project.interface').ProjectFull} Project
 * @typedef {import('@/models/project.interface').SeparateProject} SeparateProject
 * @typedef {import('@/models/project.interface').ProjectSettings} ProjectSettings
 * @typedef {import('@/models/assignment.interface').AssignmentFull} ProjectAssignment
 * @typedef {import('@/models/assignment.interface').Settings} Settings
 * @typedef {import('../types/additional-request-options.interface').AdditionalRequestOptions} AdditionalRequestOptions
 */

export class ProjectApi extends ApiBase {
  /**
   * @private
   * @readonly
   */
  PROJECTS_API_ROOT = 'projects';

  /**
   * @param {string} employeeId
   * @param {string} officeId
   * @returns {Promise<AxiosResponse<Project[]>>}
   */
  getProjects = (employeeId, officeId) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.PROJECTS_API_ROOT}/`,
    params: {
      employee_id: employeeId,
      office_id: officeId,
    },
  });

  /**
   * @param {string} employeeId
   * @param {string} officeId
   * @returns {Promise<AxiosResponse<Project[]>>}
   */
  getProjectsByViewer = (employeeId, officeId) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.PROJECTS_API_ROOT}/by-access/view-project-details`,
    params: {
      employee_id: employeeId,
      office_id: officeId,
    },
  });

  /**
   * @param {string} projectId
   * @returns {Promise<AxiosResponse<SeparateProject>>}
   */
  getProjectById = (projectId) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.PROJECTS_API_ROOT}/${projectId}`,
  }, { loadingType: loadingTypes.syncProjectButton });

  /**
   * @param {string} employeeId
   * @param {string} projectId
   * @param {string} roleId
   * @returns {Promise<AxiosResponse<void>>}
   */
  deleteProjectEmployee = (projectId, employeeId, roleId) => this.apiClient.sendRequest({
    method: 'DELETE',
    url: `${this.PROJECTS_API_ROOT}/${projectId}/employees/${employeeId}/roles/${roleId}/`,
  });

  /**
   * @param {string} employeeId
   * @param {string} projectId
   * @param {string} roleId
   * @returns {Promise<AxiosResponse<void>>}
   */
  putEmployeeProjectRole = (projectId, employeeId, roleId) => this.apiClient.sendRequest({
    method: 'PUT',
    url: `${this.PROJECTS_API_ROOT}/${projectId}/employees/${employeeId}/roles/${roleId}/`,
  });

  /**
   * @param {string} projectId
   * @param {AdditionalRequestOptions} [options]
   * @returns {Promise<AxiosResponse<ProjectAssignment>>}
   */
  getProjectAssignments = (projectId, options) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.PROJECTS_API_ROOT}/${projectId}/assignments/`,
  }, options);

  /**
   * @param {string} projectId
   * @param {string} assignmentId
   * @param {Settings | Record<string, string>} settings
   * @returns {Promise<AxiosResponse<void>>}
   */
  setProjectAssignmentSettings = (projectId, assignmentId, settings) => this.apiClient.sendRequest({
    method: 'PUT',
    url: `${this.PROJECTS_API_ROOT}/${projectId}/assignment/${assignmentId}/settings/`,
    data: settings,
  });

  /**
   * @param {string} projectId
   * @returns {Promise<AxiosResponse<ProjectSettings>>}
   */
  getProjectSettingsById = (projectId) => this.apiClient.sendRequest({
    method: 'GET',
    url: `${this.PROJECTS_API_ROOT}/${projectId}/settings/`,
  }, { loadingType: loadingTypes.syncProjectButton });

  // /**
  //  * @param {string} projectId
  //  * @param {string} settings
  //  // * @returns {Promise<AxiosResponse<T>>}
  //  */
  updateAllowTimeLogsForRemovedEmployees = (projectId, setting) => this.apiClient.sendRequest({
    method: 'PUT',
    url: `${this.PROJECTS_API_ROOT}/${projectId}/settings/allow_time_logs_for_removed_employees/`,
    data: setting,
  });
  updateAllowTimeLogsInFinishedProject = (projectId, setting) => this.apiClient.sendRequest({
    method: 'PUT',
    url: `${this.PROJECTS_API_ROOT}/${projectId}/settings/allow_time_logs_in_finished_project/`,
    data: setting,
  });
}
